import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import * as React from 'react';

import type {
  OperationalDetailRowDto,
  ProfileMatrixRowDto,
  RateSeniorityLevelDto,
} from '@/api/operational-view-client';
import {
  useCards,
  useProfileDetail,
  useProfileMatrix,
} from '@/api/use-operational-view';
import { useCampaignDetail } from '@/api/use-campaigns';
import { useCatalogProfiles } from '@/api/use-catalog-edition';
import { Button } from '@/components/ui/button';
import { FilterCombo } from '@/components/ui/filter-combo';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Panel } from '@/components/ui/panel';
import { filterTriggerLabel } from '@/lib/filter-trigger-label';
import {
  SENIORITY_LEVELS_ORDERED,
  HORS_JEUX_EXPLANATION,
  formatEur,
  seniorityLabel,
} from '@/lib/operational-view-labels';
import { OperationalViewHeading } from '@/routes/admin/operational-view/operational-view-cards';
import {
  OperationalViewComparisonPanel,
  OperationalViewComparisonToggle,
} from '@/routes/admin/operational-view/operational-view-comparison-panel';
import {
  FilterPopover,
  RadioOption,
} from '@/routes/admin/operational-view/operational-view-filter-menu';
import { useDisplayPreferences } from '@/routes/admin/operational-view/operational-view-preferences';
import {
  ColumnsMenu,
  EmptyCell,
  HorsJeuBadge,
  OperationalTable,
  PanelFooter,
  PanelMessage,
  PanelToolbar,
  StaleCycleBadge,
  toTableState,
} from '@/routes/admin/operational-view/operational-view-table';

// Ids des colonnes MASQUABLES de la matrice (Section 2). `profileCode` (en-tête de ligne / sélection)
// reste TOUJOURS visible (enableHiding:false) ⇒ hors de cette liste.
const MATRIX_HIDEABLE_IDS = [
  'seniorityLevel',
  'targetPriceEur',
  'medianEur',
  'avgEur',
  'minEur',
  'maxEur',
  'stddevEur',
  'responseRatePct',
  'horsJeuxSupplierCount',
  'aboveP75SupplierCount',
  'cycleStale',
] as const;

/** Colonnes chiffrées de la matrice (valeurs alignées à droite, tabular-nums). */
const MATRIX_NUMERIC_IDS: ReadonlySet<string> = new Set([
  'targetPriceEur',
  'medianEur',
  'avgEur',
  'minEur',
  'maxEur',
  'stddevEur',
  'responseRatePct',
  'horsJeuxSupplierCount',
  'aboveP75SupplierCount',
]);

/** Id de la région « Comparaison avec les campagnes antérieures » (cible aria-controls du lien de pied). */
const COMPARISON_PANEL_ID = 'opv-profils-comparison';

// =============================================================================
// operational-view-profils-screen.tsx — Page Profils §2.5 v2.2 (S14-bis / C1).
// =============================================================================
// Refonte §2.5 v2.2 : la machinerie « 4 codes couleurs » est SUPPRIMÉE. L'information ne passe
// JAMAIS par une couleur seule (Cx-3, WCAG 1.4.1) : les états Hors Jeux et « cycle non
// actualisé » portent un LIBELLÉ TEXTE explicite (badge), la couleur n'est qu'un renfort.
//
//   §1 — Cards d'indicateurs (réutilisables, OperationalCardsPanel).
//   §2 — Matrice Profil × Niveau (useProfileMatrix) : TanStack Table + filtres serveur
//        profileCode/seniorityLevel + toggle « lignes à actualiser » (cycleStale, client-side).
//        Clic sur une ligne → sélectionne le couple → peuple §3.
//   §3 — Détail du couple sélectionné (useProfileDetail, à la demande) : par fournisseur,
//        C1/C2 + écarts + évolution. Une ligne Hors Jeux porte un badge LIBELLÉ « Hors Jeux »
//        (un voile de fond SOLIDE amber-50 est un RENFORT visuel redondant, jamais le seul porteur).
//
// L'AUTORITÉ reste l'API (calcul AU VOL) ; l'écran LIT et HABILLE. État de sélection/filtres
// IN-MEMORY React (RULE_REF_006 : pas de localStorage pour donnée métier). WCAG 2.1 AA :
// <table>/<caption>/<th scope>, labels liés (htmlFor/id), états role="status"/role="alert".
//
// RESTYLING 2026-09-17 (spec tasks/spec-restyling-vue-operationnelle-20260917.md, GATE_HUMAN « simple
// restyling, les informations à afficher sont celles présentes aujourd'hui ») — disposition seule :
//   · titre porté par l'en-tête de zone (h2 sr-only « Vue opérationnelle — Profils ») ;
//   · bascule Profils | Fournisseurs + ligne de 4 indicateurs sans card ;
//   · card Panel « Matrice Profil × Niveau » + compteur : barre (Profil, Niveau ▾, Famille ▾, Sous-famille ▾,
//     « Lignes à actualiser seulement », Colonnes n / 11 ▾), règle Hors Jeux écrite UNE fois en en-tête, pied
//     avec le lien « Comparer aux campagnes antérieures » ;
//   · card de détail sous la matrice UNIQUEMENT à la sélection (Colonnes ▾, Fermer).

/** Formate un montant + son pourcentage (« 350 € (54 %) ») ou tiret si null. */
function formatEurPct(eur: number | null, pct: number | null): string {
  if (eur === null) {
    return '—';
  }
  const pctSuffix = pct === null ? '' : ` (${String(pct)} %)`;
  return `${formatEur(eur)}${pctSuffix}`;
}

/** Couple Profil × Niveau sélectionné (alimente §3). */
interface SelectedCouple {
  readonly profileCode: string;
  readonly seniorityLevel: RateSeniorityLevelDto;
}

const matrixColumnHelper = createColumnHelper<ProfileMatrixRowDto>();

export interface OperationalViewProfilsScreenProps {
  readonly campaignId: string;
  /** Navigation vers la page sœur Fournisseurs (porté par l'adaptateur routeur). */
  readonly onViewFournisseurs?: () => void;
}

export function OperationalViewProfilsScreen({
  campaignId,
  onViewFournisseurs,
}: OperationalViewProfilsScreenProps): React.JSX.Element {
  // --- Filtres serveur (F) : profil/séniorité — état IN-MEMORY (RULE_REF_006) ---
  const [profileCodeInput, setProfileCodeInput] = React.useState('');
  const [seniorityInput, setSeniorityInput] =
    React.useState<RateSeniorityLevelDto | ''>('');
  // Toggle CLIENT-SIDE « n'afficher que les lignes à actualiser » (cycleStale).
  const [staleOnly, setStaleOnly] = React.useState(false);
  // Couple sélectionné pour le détail §3 (null = aucun ⇒ §3 vide, requête désactivée).
  const [selected, setSelected] = React.useState<SelectedCouple | null>(null);
  // C1-bis : filtre Famille (N1) / Sous-famille (N2) — sélections multiples INDÉPENDANTES (ET),
  // client-side. Lot F1 (2026-09-21) : la structure passe du `Set` à la LISTE, forme attendue par la
  // liste autofiltrante `FilterCombo` (qui la rend dans l'ordre alphabétique de ses options).
  const [selectedFamilles, setSelectedFamilles] = React.useState<readonly string[]>([]);
  const [selectedSousFamilles, setSelectedSousFamilles] = React.useState<readonly string[]>(
    [],
  );

  // Filtres serveur de §1/§2 : on omet les valeurs vides pour ne pas envoyer de query inutile.
  const matrixFilters = React.useMemo(
    () => ({
      ...(profileCodeInput !== '' ? { profileCode: profileCodeInput } : {}),
      ...(seniorityInput !== '' ? { seniorityLevel: seniorityInput } : {}),
    }),
    [profileCodeInput, seniorityInput],
  );

  // Filtres serveur de §3 : restreints au couple sélectionné (profil + niveau exacts).
  const detailFilters = React.useMemo(
    () =>
      selected === null
        ? {}
        : {
            profileCode: selected.profileCode,
            seniorityLevel: selected.seniorityLevel,
          },
    [selected],
  );

  const cardsQuery = useCards(campaignId, matrixFilters);
  const matrixQuery = useProfileMatrix(campaignId, matrixFilters);
  const detailQuery = useProfileDetail(campaignId, detailFilters, selected !== null);

  // C1-bis — jointure FRONT-SIDE Famille/Sous-famille. Source : le catalogue FIGÉ de la campagne
  // (catalogVersionId du DÉTAIL campagne, PAS le catalogue courant) ⇒ matrice et filtre lisent la MÊME
  // version. Aucun endpoint analytics paramétré famille (pas de recouplage analytics↔catalogue).
  const detail = useCampaignDetail(campaignId);
  const catalogVersionId = detail.data?.catalogVersionId ?? '';
  const profilesQuery = useCatalogProfiles(
    catalogVersionId,
    catalogVersionId !== '',
  );
  const familleMap = React.useMemo(() => {
    const map = new Map<string, { famille: string; sousFamille: string }>();
    for (const p of profilesQuery.data ?? []) {
      map.set(p.profileCode, { famille: p.famille, sousFamille: p.sousFamille });
    }
    return map;
  }, [profilesQuery.data]);

  // LOV scopées au PÉRIMÈTRE (familles/sous-familles présentes dans la matrice jointe, pas tout le catalogue).
  const baseRows = React.useMemo(
    () => matrixQuery.data?.rows ?? [],
    [matrixQuery.data?.rows],
  );
  const lovFamilles = React.useMemo(
    () =>
      [
        ...new Set(
          baseRows
            .map((r) => familleMap.get(r.profileCode)?.famille)
            .filter((f): f is string => f !== undefined),
        ),
      ].sort(),
    [baseRows, familleMap],
  );
  const lovSousFamilles = React.useMemo(
    () =>
      [
        ...new Set(
          baseRows
            .map((r) => familleMap.get(r.profileCode)?.sousFamille)
            .filter((f): f is string => f !== undefined),
        ),
      ].sort(),
    [baseRows, familleMap],
  );

  // Filtres CLIENT-SIDE composés : cycleStale (toggle) PUIS Famille (N1) PUIS Sous-famille (N2), en ET.
  // FAIL-SAFE ORPHELIN : un profileCode absent du catalogue (famille undefined) ne passe AUCUN filtre
  // famille actif (exclu) ; il reste VISIBLE quand aucune famille/sous-famille n'est sélectionnée.
  const matrixRows = React.useMemo(() => {
    let rows = matrixQuery.data?.rows ?? [];
    if (staleOnly) {
      rows = rows.filter((r) => r.cycleStale);
    }
    if (selectedFamilles.length > 0) {
      rows = rows.filter((r) => {
        const f = familleMap.get(r.profileCode)?.famille;
        return f !== undefined && selectedFamilles.includes(f);
      });
    }
    if (selectedSousFamilles.length > 0) {
      rows = rows.filter((r) => {
        const sf = familleMap.get(r.profileCode)?.sousFamille;
        return sf !== undefined && selectedSousFamilles.includes(sf);
      });
    }
    return rows;
  }, [
    matrixQuery.data?.rows,
    staleOnly,
    selectedFamilles,
    selectedSousFamilles,
    familleMap,
  ]);


  const columns = React.useMemo(
    () => [
      matrixColumnHelper.accessor('profileCode', {
        id: 'profileCode',
        header: 'Profil',
        cell: (info) => info.getValue(),
        // En-tête de ligne + cible de sélection ⇒ toujours visible (non masquable).
        enableHiding: false,
      }),
      matrixColumnHelper.accessor('seniorityLevel', {
        id: 'seniorityLevel',
        header: 'Niveau',
        cell: (info) => seniorityLabel(info.getValue()),
      }),
      matrixColumnHelper.accessor('targetPriceEur', {
        id: 'targetPriceEur',
        header: 'Prix Cible',
        cell: (info) => formatEur(info.getValue()),
      }),
      matrixColumnHelper.accessor('medianEur', {
        id: 'medianEur',
        header: 'Médiane',
        cell: (info) => formatEur(info.getValue()),
      }),
      matrixColumnHelper.accessor('avgEur', {
        id: 'avgEur',
        header: 'Moyenne',
        cell: (info) => formatEur(info.getValue()),
      }),
      matrixColumnHelper.accessor('minEur', {
        id: 'minEur',
        header: 'Min',
        cell: (info) => formatEur(info.getValue()),
      }),
      matrixColumnHelper.accessor('maxEur', {
        id: 'maxEur',
        header: 'Max',
        cell: (info) => formatEur(info.getValue()),
      }),
      matrixColumnHelper.accessor('stddevEur', {
        id: 'stddevEur',
        header: 'Écart-type',
        cell: (info) => formatEur(info.getValue()),
      }),
      matrixColumnHelper.accessor('responseRatePct', {
        id: 'responseRatePct',
        header: 'Taux de réponse',
        cell: (info) => `${String(info.getValue())} %`,
      }),
      matrixColumnHelper.accessor('horsJeuxSupplierCount', {
        id: 'horsJeuxSupplierCount',
        header: 'Hors jeu',
        cell: (info) => String(info.getValue()),
      }),
      matrixColumnHelper.accessor('aboveP75SupplierCount', {
        id: 'aboveP75SupplierCount',
        header: '> P75',
        cell: (info) => String(info.getValue()),
      }),
      matrixColumnHelper.display({
        id: 'cycleStale',
        header: 'Cycle',
        // LIBELLÉ TEXTE (jamais couleur/teinte seule, Cx-3 / WCAG 1.4.1). Cycle à jour ⇒ tiret.
        cell: (ctx) =>
          ctx.row.original.cycleStale ? <StaleCycleBadge /> : <EmptyCell />,
      }),
    ],
    [],
  );

  // Cx-6 : tri + masquage de la Section 2 PERSISTÉS (localStorage v2, clé « profils »). Les filtres
  // (profil/séniorité/stale) ne sont PAS persistés (décision GATE_HUMAN C3 : recherche ≠ préférence §503).
  const [prefs, setPrefs] = useDisplayPreferences('profils', MATRIX_HIDEABLE_IDS);
  const { visibility, sorting } = React.useMemo(
    () => toTableState(prefs.hiddenColumns, prefs.sort),
    [prefs.hiddenColumns, prefs.sort],
  );

  const table = useReactTable({
    data: matrixRows as ProfileMatrixRowDto[],
    columns,
    state: { sorting, columnVisibility: visibility },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      setPrefs({
        ...prefs,
        sort: next.length === 0 ? null : { id: next[0]!.id, desc: next[0]!.desc },
      });
    },
    onColumnVisibilityChange: (updater) => {
      const next =
        typeof updater === 'function' ? updater(visibility) : updater;
      setPrefs({
        ...prefs,
        hiddenColumns: MATRIX_HIDEABLE_IDS.filter((id) => next[id] === false),
      });
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Déclencheur de la sélection (bouton d'en-tête de ligne) : le focus y revient à la fermeture du détail.
  const selectionTriggerRef = React.useRef<HTMLButtonElement | null>(null);
  const selectCouple = React.useCallback(
    (row: ProfileMatrixRowDto, trigger: HTMLButtonElement | null): void => {
      selectionTriggerRef.current = trigger;
      setSelected({
        profileCode: row.profileCode,
        seniorityLevel: row.seniorityLevel,
      });
    },
    [],
  );
  const closeDetail = React.useCallback((): void => {
    setSelected(null);
    const trigger = selectionTriggerRef.current;
    if (trigger?.isConnected === true) {
      trigger.focus();
    }
  }, []);

  // Ligne de matrice du couple sélectionné : alimente le résumé du détail (valeurs DÉJÀ affichées).
  const selectedMatrixRow = React.useMemo(
    () =>
      selected === null
        ? undefined
        : matrixQuery.data?.rows.find(
            (r) =>
              r.profileCode === selected.profileCode &&
              r.seniorityLevel === selected.seniorityLevel,
          ),
    [selected, matrixQuery.data?.rows],
  );

  // §4 — Comparaison antérieure : repliée par défaut, dépliée par le lien de pied de matrice.
  const [comparisonExpanded, setComparisonExpanded] = React.useState(false);

  return (
    <div className="space-y-6">
      {/* §1 — Titre de page (nom accessible conservé, visuel porté par l'en-tête de zone), bascule, indicateurs. */}
      <OperationalViewHeading
        title="Vue opérationnelle — Profils"
        switchProps={{ current: 'profils', onViewFournisseurs }}
        cards={{
          isPending: cardsQuery.isPending,
          isError: cardsQuery.isError,
          errorMessage: cardsQuery.error?.message,
          cards: cardsQuery.data,
        }}
      />

      {/* §2 — Matrice Profil × Niveau : barre de filtres + table (clic ligne → §3) + pied (comparaison). */}
      <Panel
        headingId="opv-profils-matrix-heading"
        title="Matrice Profil × Niveau"
        count={matrixQuery.isSuccess ? matrixRows.length : undefined}
        aside={<p className="max-w-xl text-xs">{HORS_JEUX_EXPLANATION}</p>}
      >
        <PanelToolbar>
          <Label htmlFor="filter-profile" className="sr-only">
            Profil
          </Label>
          <Input
            id="filter-profile"
            type="text"
            value={profileCodeInput}
            onChange={(e) => setProfileCodeInput(e.target.value)}
            placeholder="Profil…"
            className="h-9 w-40"
          />
          <FilterPopover
            triggerLabel={`Niveau · ${
              seniorityInput === '' ? 'tous' : seniorityLabel(seniorityInput)
            }`}
            groupLabel="Niveau"
            groupRole="radiogroup"
          >
            <RadioOption
              id="filter-seniority-all"
              name="filter-seniority"
              label="Tous"
              checked={seniorityInput === ''}
              onChange={() => setSeniorityInput('')}
            />
            {SENIORITY_LEVELS_ORDERED.map((level) => (
              <RadioOption
                key={level}
                id={`filter-seniority-${level}`}
                name="filter-seniority"
                label={seniorityLabel(level)}
                checked={seniorityInput === level}
                onChange={() => setSeniorityInput(level)}
              />
            ))}
          </FilterPopover>
          {/* Famille (N1) / Sous-famille (N2) : sélections multiples INDÉPENDANTES (ET), absentes si
              LOV vide. Lot F1 (2026-09-21) : elles étaient DÉJÀ multiples ; elles gagnent le CHAMP DE
              FILTRE de `FilterCombo` (le catalogue d'une campagne réelle compte des dizaines de
              familles — la liste seule n'était plus praticable). */}
          {lovFamilles.length > 0 ? (
            <FilterCombo
              triggerLabel={filterTriggerLabel('Famille', selectedFamilles, 'toutes')}
              groupLabel="Famille (N1)"
              options={lovFamilles.map((famille) => ({ value: famille, label: famille }))}
              selected={selectedFamilles}
              onChange={setSelectedFamilles}
              searchPlaceholder="Rechercher une famille…"
            />
          ) : null}
          {lovSousFamilles.length > 0 ? (
            <FilterCombo
              triggerLabel={filterTriggerLabel(
                'Sous-famille',
                selectedSousFamilles,
                'toutes',
              )}
              groupLabel="Sous-famille (N2)"
              options={lovSousFamilles.map((sousFamille) => ({
                value: sousFamille,
                label: sousFamille,
              }))}
              selected={selectedSousFamilles}
              onChange={setSelectedSousFamilles}
              searchPlaceholder="Rechercher une sous-famille…"
            />
          ) : null}
          <div className="flex items-center gap-2">
            <input
              id="filter-stale-only"
              type="checkbox"
              className="size-4 rounded border-input"
              checked={staleOnly}
              onChange={(e) => setStaleOnly(e.target.checked)}
            />
            <Label htmlFor="filter-stale-only" className="font-normal">
              Lignes à actualiser seulement
            </Label>
          </div>
          <div className="ml-auto">
            {/* Masquage de colonnes (persisté localStorage v2, clé « profils »). */}
            <ColumnsMenu table={table} idPrefix="profils-matrix" />
          </div>
        </PanelToolbar>

        {matrixQuery.isPending ? (
          <PanelMessage kind="status">Chargement de la matrice…</PanelMessage>
        ) : matrixQuery.isError ? (
          <PanelMessage kind="alert">
            Erreur de chargement : {matrixQuery.error.message}
          </PanelMessage>
        ) : matrixRows.length === 0 ? (
          <PanelMessage kind="status">
            Aucun couple Profil × Niveau pour le filtre courant.
          </PanelMessage>
        ) : (
          <OperationalTable
            table={table}
            caption="Matrice des couples Profil × Niveau : Prix Cible (repère), médiane, moyenne, min, max, écart-type, taux de réponse, nombre de fournisseurs Hors Jeux (au-dessus de la médiane du couple + seuil), nombre au-dessus du P75 et état du cycle."
            numericColumnIds={MATRIX_NUMERIC_IDS}
            selection={{
              isSelected: (row) =>
                selected !== null &&
                selected.profileCode === row.profileCode &&
                selected.seniorityLevel === row.seniorityLevel,
              onSelect: selectCouple,
            }}
          />
        )}

        <PanelFooter
          action={
            <OperationalViewComparisonToggle
              expanded={comparisonExpanded}
              onToggle={() => setComparisonExpanded((v) => !v)}
              controlsId={COMPARISON_PANEL_ID}
            />
          }
        >
          Statistiques calculées au vol par l’API sur les réponses soumises (médiane,
          moyenne, min, max, écart-type, taux de réponse), avec le Prix Cible en
          repère. Sélectionnez une ligne pour afficher le détail par fournisseur.
        </PanelFooter>
      </Panel>

      {/* §3 — Détail du couple sélectionné : card absente sans sélection. */}
      {selected !== null ? (
        <ProfileDetailPanel
          selected={selected}
          matrixRow={selectedMatrixRow}
          isPending={detailQuery.isPending}
          isError={detailQuery.isError}
          errorMessage={detailQuery.error?.message}
          rows={detailQuery.data?.rows}
          onClose={closeDetail}
        />
      ) : null}

      {/* §4 — Comparaison OPTIONNELLE avec les campagnes antérieures (aucune lecture tant que repliée). */}
      <OperationalViewComparisonPanel
        campaignId={campaignId}
        expanded={comparisonExpanded}
        panelId={COMPARISON_PANEL_ID}
      />
    </div>
  );
}

// `selectionSummary` (résumé local du déclencheur) a été RETIRÉ au lot F1 : sa grammaire est celle de
// `lib/filter-trigger-label.ts`, désormais partagée par les quatre écrans de la zone campagne.

// --- §3 — Détail du couple sélectionné (par fournisseur) ----------------------

interface ProfileDetailPanelProps {
  readonly selected: SelectedCouple;
  /** Ligne de matrice du couple (résumé des valeurs déjà affichées dans la matrice). */
  readonly matrixRow: ProfileMatrixRowDto | undefined;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly errorMessage: string | undefined;
  readonly rows: readonly OperationalDetailRowDto[] | undefined;
  readonly onClose: () => void;
}

const detailColumnHelper = createColumnHelper<OperationalDetailRowDto>();

// Colonnes Section 3 page Profils (ordre DBU l.1171). Triables + masquables (état LOCAL, NON persisté —
// le détail est éphémère, re-peuplé à chaque sélection). `Fournisseur` = en-tête de ligne, non masquable.
const profileDetailColumns: ColumnDef<OperationalDetailRowDto>[] = [
  detailColumnHelper.accessor('raisonSociale', {
    id: 'fournisseur',
    header: 'Fournisseur',
    enableHiding: false,
    cell: (info) => {
      const row = info.row.original;
      const label = row.raisonSociale === '' ? row.supplierSiret : row.raisonSociale;
      return (
        <>
          <span>{label}</span>
          <span className="block font-mono text-xs font-normal text-muted-foreground">
            {row.supplierSiret}
          </span>
        </>
      );
    },
  }),
  detailColumnHelper.accessor('responseC1Eur', {
    id: 'responseC1Eur',
    header: 'Réponse C1',
    cell: (info) => formatEur(info.getValue()),
  }),
  detailColumnHelper.accessor('responseC2Eur', {
    id: 'responseC2Eur',
    header: 'Réponse C2',
    cell: (info) => formatEur(info.getValue()),
  }),
  detailColumnHelper.accessor('respondentCount', {
    id: 'respondentCount',
    header: 'Nb répondants',
    cell: (info) => String(info.getValue()),
  }),
  detailColumnHelper.accessor('medianEur', {
    id: 'medianEur',
    header: 'Médiane',
    cell: (info) => formatEur(info.getValue()),
  }),
  detailColumnHelper.accessor('targetPriceEur', {
    id: 'targetPriceEur',
    header: 'Prix Cible',
    cell: (info) => formatEur(info.getValue()),
  }),
  detailColumnHelper.accessor('ecartTargetEur', {
    id: 'ecartTargetEur',
    header: 'Écart Prix Cible',
    cell: (info) =>
      formatEurPct(info.getValue(), info.row.original.ecartTargetPct),
  }),
  detailColumnHelper.accessor('ecartMedianEur', {
    id: 'ecartMedianEur',
    header: 'Écart Médiane',
    cell: (info) =>
      formatEurPct(info.getValue(), info.row.original.ecartMedianPct),
  }),
  detailColumnHelper.accessor('evolutionC1C2Eur', {
    id: 'evolutionC1C2Eur',
    header: 'Évolution C1→C2',
    cell: (info) =>
      formatEurPct(info.getValue(), info.row.original.evolutionC1C2Pct),
  }),
  detailColumnHelper.display({
    id: 'horsJeux',
    header: 'État',
    cell: (ctx) => (ctx.row.original.horsJeux ? <HorsJeuBadge /> : <EmptyCell />),
  }),
];

/** Colonnes chiffrées du détail Profils (alignées à droite). */
const PROFILE_DETAIL_NUMERIC_IDS: ReadonlySet<string> = new Set([
  'responseC1Eur',
  'responseC2Eur',
  'respondentCount',
  'medianEur',
  'targetPriceEur',
  'ecartTargetEur',
  'ecartMedianEur',
  'evolutionC1C2Eur',
]);

function ProfileDetailPanel({
  selected,
  matrixRow,
  isPending,
  isError,
  errorMessage,
  rows,
  onClose,
}: ProfileDetailPanelProps): React.JSX.Element {
  // Tri + masquage LOCAUX (non persistés : le détail change à chaque sélection).
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});

  const table = useReactTable({
    data: (rows ?? []) as OperationalDetailRowDto[],
    columns: profileDetailColumns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Résumé mono : valeurs DÉJÀ affichées sur la ligne de matrice (aucune donnée nouvelle).
  const summary =
    matrixRow === undefined
      ? null
      : [
          `Médiane ${formatEur(matrixRow.medianEur)}`,
          `Prix Cible ${formatEur(matrixRow.targetPriceEur)}`,
          `Taux de réponse ${String(matrixRow.responseRatePct)} %`,
          `Hors jeu ${String(matrixRow.horsJeuxSupplierCount)}`,
        ].join(' · ');

  return (
    <Panel
      headingId="opv-profils-detail-heading"
      title={`${selected.profileCode} × ${seniorityLabel(selected.seniorityLevel)}`}
      aside={
        <>
          {summary !== null ? (
            <span className="font-mono text-xs">{summary}</span>
          ) : null}
          <ColumnsMenu table={table} idPrefix="profils-detail" withCount={false} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            className="text-foreground"
          >
            Fermer
          </Button>
        </>
      }
    >
      {isPending ? (
        <PanelMessage kind="status">Chargement du détail…</PanelMessage>
      ) : isError ? (
        <PanelMessage kind="alert">Erreur de chargement : {errorMessage}</PanelMessage>
      ) : (rows?.length ?? 0) === 0 ? (
        <PanelMessage kind="status">Aucun fournisseur pour ce couple.</PanelMessage>
      ) : (
        <OperationalTable
          table={table}
          caption="Détail par fournisseur du couple sélectionné : réponse C1, réponse C2, nombre de répondants, médiane, Prix Cible (repère), écart au Prix Cible, écart à la médiane, évolution C1→C2 et état Hors Jeux (au-dessus de la médiane du couple + seuil)."
          numericColumnIds={PROFILE_DETAIL_NUMERIC_IDS}
          isOutOfPlay={(row) => row.horsJeux}
        />
      )}
      <PanelFooter>
        Détail par fournisseur du couple sélectionné : réponses C1/C2, écarts à la
        médiane et au Prix Cible (repère), évolution C1→C2. Colonnes triables et
        masquables.
      </PanelFooter>
    </Panel>
  );
}
